import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconCash } from '../components/icons';
import {
  Avatar,
  Chips,
  DataTable,
  Empty,
  ErrorNote,
  FlashNote,
  Loading,
  PageHead,
  Pager,
  Pill,
  ReasonDialog,
  RowCard,
  SearchField,
  Stat,
  useFlash,
} from '../components/ui';
import { api } from '../lib/api';
import { ago, dateTime, phone, rupees } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { BalancePage, BalanceRow, Page, Payout, PayoutStatus } from '../lib/types';
import { payoutsRoute } from '../router';
import { CashInDialog, MarkPaidDialog, METHOD_LABEL, PayDialog } from './payoutForms';

type Filter = 'all' | 'owes' | 'owed';
type Tab = 'balances' | 'payouts';

export function BalanceAmount({ r }: { r: { balancePaise: number; toCollectPaise: number; availableToPayPaise: number; pendingPayoutsPaise: number } }) {
  if (r.toCollectPaise > 0)
    return (
      <div>
        <div className="primary num strong" style={{ color: 'var(--warn)' }}>
          {rupees(r.toCollectPaise)}
        </div>
        <div className="secondary small muted">Driver hands in</div>
      </div>
    );
  if (r.balancePaise > 0)
    return (
      <div>
        <div className="primary num strong" style={{ color: 'var(--good)' }}>
          {rupees(r.balancePaise)}
        </div>
        <div className="secondary small muted">{r.pendingPayoutsPaise > 0 ? `${rupees(r.pendingPayoutsPaise)} waiting to be paid` : 'Pay the driver'}</div>
      </div>
    );
  return <span className="faint">Settled</span>;
}

export const PayoutStatusPill = ({ s }: { s: PayoutStatus }) =>
  s === 'PAID' ? <Pill tone="good">Paid</Pill> : s === 'PENDING' ? <Pill tone="warn">Waiting to be paid</Pill> : <Pill>Cancelled</Pill>;

/**
 * Money between drivers and Zoomo Rides (cash only). Cash rides leave Zoomo Rides' commission with the driver;
 * fares paid out, tips and bonuses leave Zoomo Rides owing the driver. Staff record cash handed in, create payouts
 * (UPI, bank or cash) and mark them paid with the UTR.
 */
export function PayoutsPage() {
  const search = payoutsRoute.useSearch();
  const nav = payoutsRoute.useNavigate();
  const tab: Tab = search.tab ?? 'balances';
  const [flash, show] = useFlash(9000);

  return (
    <div className="stack">
      <PageHead title="Payouts" sub="What each driver and Zoomo Rides owe each other, cash handed in, and payouts to drivers." />
      <Totals />
      <Chips<Tab>
        label="Show"
        value={tab}
        onChange={(v) => nav({ search: { tab: v === 'balances' ? undefined : v } })}
        options={[
          { value: 'balances', label: 'Balances' },
          { value: 'payouts', label: 'Payouts' },
        ]}
      />
      <FlashNote flash={flash} testId="settle-done" />
      {tab === 'balances' ? <Balances onDone={(t) => show(t)} /> : <PayoutList onDone={(t) => show(t)} />}
    </div>
  );
}

function Totals() {
  const q = useQuery({
    queryKey: [...qk.payouts, 'totals'],
    queryFn: () => api.get<BalancePage>('/admin/payouts/balances', { pageSize: 1 }),
    refetchInterval: 30_000,
  });
  const t = q.data?.totals;
  return (
    <div className="stats stats-3">
      <Stat icon={IconCash} label="Drivers hold (to collect)" testId="cash-to-collect" value={t ? rupees(t.driversOwePaise) : '—'} foot="Commission from cash rides" />
      <Stat label="Zoomo Rides owes drivers" value={t ? rupees(t.zoomoOwesPaise) : '—'} foot="Earnings, tips, bonuses" />
      <Stat label="Payouts waiting" value={t ? rupees(t.pendingPayoutsPaise) : '—'} foot="Created, not yet marked paid" attn={!!t?.pendingPayoutsPaise} />
    </div>
  );
}

function Balances({ onDone }: { onDone: (t: string) => void }) {
  const search = payoutsRoute.useSearch();
  const nav = payoutsRoute.useNavigate();
  const navigate = useNavigate();
  const filter: Filter = search.filter ?? 'all';
  const page = search.page ?? 1;
  const [text, setText] = useState(search.q ?? '');
  const [acting, setActing] = useState<{ row: BalanceRow; kind: 'pay' | 'cash' } | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text.trim()) void nav({ search: { ...search, q: text.trim() || undefined, page: undefined } });
    }, 300);
    return () => clearTimeout(t);
  }, [text, search, nav]);

  const q = useQuery({
    queryKey: [...qk.payouts, 'balances', { filter, q: search.q, page }],
    queryFn: () => api.get<BalancePage>('/admin/payouts/balances', { filter, search: search.q, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });

  const actions = (r: BalanceRow) => (
    <div className="row" style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
      {r.toCollectPaise > 0 && (
        <button className="btn btn-sm btn-dark" onClick={(e) => (e.stopPropagation(), setActing({ row: r, kind: 'cash' }))}>
          Record cash in
        </button>
      )}
      {r.availableToPayPaise > 0 && (
        <button className="btn btn-sm btn-dark" onClick={(e) => (e.stopPropagation(), setActing({ row: r, kind: 'pay' }))}>
          Pay {rupees(r.availableToPayPaise)}
        </button>
      )}
    </div>
  );
  const open = (r: BalanceRow) => navigate({ to: '/payouts/$driverId', params: { driverId: r.driver.id } });

  return (
    <>
      <div className="toolbar">
        <Chips<Filter>
          label="Who"
          value={filter}
          onChange={(v) => void nav({ search: { ...search, filter: v === 'all' ? undefined : v, page: undefined } })}
          options={[
            { value: 'all', label: 'Everyone' },
            { value: 'owes', label: 'Driver owes' },
            { value: 'owed', label: 'We owe' },
          ]}
        />
        <SearchField value={text} onChange={setText} placeholder="Search name, phone, email or plate" label="Search drivers" />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={5} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title="All settled" icon={IconCash}>
            {search.q ? 'No driver with a balance matches.' : 'No driver has cash to hand in or money to be paid.'}
          </Empty>
        ) : (
          <>
            <DataTable
              label="Balances"
              rows={q.data.items}
              rowKey={(r) => r.driver.id}
              rowTestId={(r) => `cash-${r.driver.id}`}
              onRowClick={open}
              columns={[
                {
                  key: 'driver',
                  header: 'Driver',
                  cell: (r) => (
                    <div className="person">
                      <Avatar name={r.driver.name} />
                      <div style={{ minWidth: 0 }}>
                        <div className="primary truncate">{r.driver.name ?? 'No name yet'}</div>
                        <div className="secondary truncate">
                          {r.driver.phone ? phone(r.driver.phone) : (r.driver.email ?? '—')}
                          {r.driver.vehicle ? ` · ${r.driver.vehicle.plateDisplay}` : ''}
                        </div>
                      </div>
                    </div>
                  ),
                },
                { key: 'amount', header: 'Balance', cell: (r) => <BalanceAmount r={r} /> },
                {
                  key: 'last',
                  header: 'Last money',
                  className: 'wide-only',
                  cell: (r) => (
                    <div className="small">
                      <div>{r.lastPayoutAt ? `Paid ${ago(r.lastPayoutAt)}` : 'Never paid'}</div>
                      <div className="muted">{r.lastHandInAt ? `Cash in ${ago(r.lastHandInAt)}` : 'No cash in yet'}</div>
                    </div>
                  ),
                },
                { key: 'act', header: '', className: 'right', cell: actions },
              ]}
              card={(r) => (
                <RowCard
                  lead={<Avatar name={r.driver.name} />}
                  title={r.driver.name ?? 'No name yet'}
                  sub={`${r.driver.vehicle?.plateDisplay ?? phone(r.driver.phone)} · ${r.lastPayoutAt ? `paid ${ago(r.lastPayoutAt)}` : 'never paid'}`}
                  side={<BalanceAmount r={r} />}
                />
              )}
              phoneExtra={(r) => (r.toCollectPaise > 0 || r.availableToPayPaise > 0 ? actions(r) : null)}
            />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
      {acting?.kind === 'cash' && <CashInDialog driver={acting.row.driver} max={acting.row.toCollectPaise} onClose={() => setActing(null)} onDone={onDone} />}
      {acting?.kind === 'pay' && <PayDialog driver={acting.row.driver} money={acting.row} onClose={() => setActing(null)} onDone={onDone} />}
    </>
  );
}

type StatusFilter = 'ALL' | PayoutStatus;

export function PayoutRows({ items, onDone, showDriver = true }: { items: Payout[]; onDone: (t: string) => void; showDriver?: boolean }) {
  const [paying, setPaying] = useState<Payout | null>(null);
  const [cancelling, setCancelling] = useState<Payout | null>(null);
  const actions = (p: Payout) =>
    p.status === 'PENDING' ? (
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
        <button className="btn btn-sm" onClick={() => setCancelling(p)}>
          Cancel
        </button>
        <button className="btn btn-sm btn-dark" onClick={() => setPaying(p)}>
          Mark paid
        </button>
      </div>
    ) : null;
  const driverId = (p: Payout) => p.driver?.id ?? '';
  return (
    <>
      <DataTable
        label="Payouts"
        rows={items}
        rowKey={(p) => p.id}
        rowTestId={(p) => `payout-${p.id}`}
        columns={[
          ...(showDriver
            ? [
                {
                  key: 'driver',
                  header: 'Driver',
                  cell: (p: Payout) =>
                    p.driver ? (
                      <Link to="/payouts/$driverId" params={{ driverId: p.driver.id }} className="primary">
                        {p.driver.name ?? 'No name yet'}
                      </Link>
                    ) : (
                      '—'
                    ),
                },
              ]
            : []),
          {
            key: 'amount',
            header: 'Amount',
            cell: (p) => (
              <div>
                <div className="primary num strong">{rupees(p.amountPaise)}</div>
                <div className="secondary">{METHOD_LABEL[p.method]}</div>
              </div>
            ),
          },
          {
            key: 'when',
            header: 'When',
            className: 'nowrap',
            cell: (p) => (
              <div className="small">
                <div>Created {dateTime(p.createdAt)}</div>
                {p.paidAt && <div className="muted">Paid {dateTime(p.paidAt)}</div>}
              </div>
            ),
          },
          {
            key: 'ref',
            header: 'Reference',
            className: 'wide-only',
            cell: (p) => (
              <div className="small">
                <div className="num">{p.reference ?? <span className="faint">—</span>}</div>
                {(p.note || p.cancelReason) && <div className="muted truncate" style={{ maxWidth: 220 }}>{p.cancelReason ?? p.note}</div>}
              </div>
            ),
          },
          { key: 'status', header: 'Status', cell: (p) => <PayoutStatusPill s={p.status} /> },
          { key: 'act', header: '', className: 'right', cell: actions },
        ]}
        card={(p) => (
          <RowCard
            title={`${rupees(p.amountPaise)} · ${METHOD_LABEL[p.method]}`}
            sub={`${showDriver && p.driver ? `${p.driver.name ?? 'Driver'} · ` : ''}${dateTime(p.paidAt ?? p.createdAt)}${p.reference ? ` · ${p.reference}` : ''}`}
            side={<PayoutStatusPill s={p.status} />}
          />
        )}
        phoneExtra={actions}
      />
      {paying && <MarkPaidDialog payout={paying} driverId={driverId(paying)} onClose={() => setPaying(null)} onDone={onDone} />}
      {cancelling && (
        <ReasonDialog
          title={`Cancel this ${rupees(cancelling.amountPaise)} payout?`}
          body="Nothing has been taken off the driver’s balance yet; it stays owed."
          confirmLabel="Cancel payout"
          danger
          placeholder="For example: wrong account number"
          onClose={() => setCancelling(null)}
          onConfirm={async (reason) => {
            await api.post(`/admin/payouts/${cancelling.id}/cancel`, { reason });
            onDone('Payout cancelled.');
          }}
        />
      )}
    </>
  );
}

function PayoutList({ onDone }: { onDone: (t: string) => void }) {
  const search = payoutsRoute.useSearch();
  const nav = payoutsRoute.useNavigate();
  const status: StatusFilter = search.status ?? 'PENDING';
  const page = search.page ?? 1;
  const q = useQuery({
    queryKey: [...qk.payouts, 'list', { status, page }],
    queryFn: () => api.get<Page<Payout>>('/admin/payouts', { status: status === 'ALL' ? undefined : status, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <div className="toolbar">
        <Chips<StatusFilter>
          label="Status"
          value={status}
          onChange={(v) => void nav({ search: { ...search, status: v === 'PENDING' ? undefined : v, page: undefined } })}
          options={[
            { value: 'PENDING', label: 'Waiting to be paid' },
            { value: 'PAID', label: 'Paid' },
            { value: 'CANCELLED', label: 'Cancelled' },
            { value: 'ALL', label: 'All' },
          ]}
        />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={4} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title={status === 'PENDING' ? 'Nothing waiting' : 'No payouts here'} icon={IconCash}>
            {status === 'PENDING' ? 'Payouts you create appear here until you mark them paid.' : undefined}
          </Empty>
        ) : (
          <>
            <PayoutRows items={q.data.items} onDone={onDone} />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}
